import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { ConsoleScope } from "@/constants/console-scope"
import { mergeTenantBusinessSummaries, getDefaultActiveBusinessId } from "@/helpers/console-scope"
import type { AuthSession } from "@/types/auth"

export interface AuthBusiness {
  _id?: string
  name?: string
  image?: string
  location?: string
  parentBusiness?: string
  description?: string
  industry?: Array<string | { name?: string }>
  receivingAccount?: { account_number?: string }
  analytics?: Record<string, unknown>
  serviceFulfillmentRate?: number
}

export interface AuthUser {
  _id?: string | number
  name?: string | null
  email?: string | null
  phoneNumber?: string | number | null
  image?: string | null
  userType?: string | string[] | null
  businesses?: AuthBusiness[]
  [key: string]: unknown
}

interface AuthState {
  user: AuthUser | null
  token: string | null
  refreshToken: string | null
  activeBusinessId: string | null
  /** PLATFORM (admin console) vs TENANT (business dashboard); null until chosen or loaded from server */
  consoleScope: ConsoleScope | null
  setCredentials: (payload: {
    user?: AuthUser | null
    accessToken?: string | null
    token?: string | null
    refreshToken?: string | null
    /** When true, clears activeBusinessId so user must select business again (use on login) */
    clearActiveBusiness?: boolean
    /** Set explicitly on login from server consoleLandingScope */
    consoleScope?: ConsoleScope | null
  }) => void
  setUser: (user: AuthUser | null) => void
  setActiveBusiness: (businessId: string | null) => void
  setConsoleScope: (scope: ConsoleScope | null) => void
  getActiveBusiness: () => AuthBusiness | null
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      activeBusinessId: null,
      consoleScope: null,
      setCredentials: (payload) =>
        set((state) => {
          let nextConsoleScope = state.consoleScope
          if (payload.consoleScope !== undefined) {
            nextConsoleScope = payload.consoleScope
          } else if (payload.clearActiveBusiness) {
            nextConsoleScope = null
          }
          return {
            user: payload.user ?? state.user,
            token: payload.accessToken ?? payload.token ?? state.token,
            refreshToken: payload.refreshToken ?? state.refreshToken,
            ...(payload.clearActiveBusiness ? { activeBusinessId: null } : {}),
            ...(payload.consoleScope !== undefined ||
            payload.clearActiveBusiness
              ? { consoleScope: nextConsoleScope }
              : {}),
          }
        }),
      setUser: (user) => set({ user }),
      setActiveBusiness: (businessId) => set({ activeBusinessId: businessId }),
      setConsoleScope: (scope) => set({ consoleScope: scope }),
      getActiveBusiness: () => {
        const state = get()
        const user = state.user
        if (!user) return null
        const merged = mergeTenantBusinessSummaries(user as AuthSession)
        if (!merged.length) return null
        if (!state.activeBusinessId) {
          const defaultId = getDefaultActiveBusinessId(user as AuthSession)
          if (defaultId) {
            return (merged.find((b) => b._id === defaultId) as AuthBusiness) ?? null
          }
          return null
        }
        const found = merged.find((b) => b._id === state.activeBusinessId)
        return (found ?? null) as AuthBusiness | null
      },
      logout: () =>
        set({
          user: null,
          token: null,
          refreshToken: null,
          activeBusinessId: null,
          consoleScope: null,
        }),
    }),
    {
      name: "auth-store",
      storage: createJSONStorage(() => localStorage),
    }
  )
)
